import {
  CATALINA_ADDRESS_TYPE,
  EMAIL_ADDRESS_TYPE,
  FEDERATION_ADDRESS_TYPE,
} from "@/constants/message";

/**
 * Recognizes the 1:1 destinations the router hands off to an external gateway:
 * Catalina (`#transponder`), federated (`user@alias`) and email (`user@example.com`).
 *
 * Each predicate mirrors its counterpart in dockerized-router and must be kept in step with it;
 * the router re-validates on every send, so anything these let through comes back as a send
 * failure rather than a delivered message. All of them trim first, matching the router.
 */

/** `FederatedGateway::addressValidation` — a username, then a single DNS label (so no dots). */
export function isFederatedAddress(address: string): boolean {
  const parts = address.trim().split("@");
  if (parts.length !== 2) return false;

  const [username, alias] = parts;
  return /^[a-z0-9_-]+$/i.test(username) && /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/i.test(alias);
}

/**
 * `_is_email_address` (`filter_var(..., FILTER_VALIDATE_EMAIL)`) — a dot-atom local part, then
 * a domain of two or more labels. Requiring the dot is what stops this claiming federated
 * addresses, and matches PHP rejecting dotless domains like `user@localhost`.
 */
export function isEmailAddress(address: string): boolean {
  return /^[A-Za-z0-9!#$%&'*+\/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+\/=?^_`{|}~-]+)*@(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.)+[A-Za-z]{2,63}$/.test(
    address.trim()
  );
}

/**
 * `_is_catalina_address` — the whole test is the leading "#". One notch stricter here: the
 * router accepts a bare "#", which can't name a transponder and would arm the Start button on
 * an unsendable conversation.
 *
 * REVISIT: this lets a user hand-type a raw transponder id. Catalina addresses realistically
 * come from a roster, so this may want to be a picker — paired with the note in constants/ui.ts.
 */
export function isCatalinaAddress(address: string): boolean {
  const trimmed = address.trim();
  return trimmed.length > 1 && trimmed.startsWith("#");
}

/**
 * The ADDRESS_TYPE to send an external destination under, or null if it isn't one.
 *
 * This is the single source of the ordering, so derive from it rather than re-testing the
 * predicates. Catalina goes first because "#" is a legal email local-part character, making
 * `#bob@example.com` match the email pattern; the router orders its own inference the same way.
 *
 * Sending the resolved type explicitly also sidesteps `SendUserMessage`'s inference for a bare
 * USERNAME, which tests `_is_email_address` before `FederatedGateway::addressValidation`.
 */
export function resolveExternalDmAddressType(address: string): number | null {
  if (isCatalinaAddress(address)) return CATALINA_ADDRESS_TYPE;
  if (isFederatedAddress(address)) return FEDERATION_ADDRESS_TYPE;
  if (isEmailAddress(address)) return EMAIL_ADDRESS_TYPE;
  return null;
}

/** True for any destination the router hands off to an external gateway. */
export function isExternalDmAddress(address: string): boolean {
  return resolveExternalDmAddressType(address) !== null;
}

/**
 * Normalizes an address the way the router stores it, so an optimistically-created conversation
 * keeps matching the row `GetConversations` later returns for it.
 *
 * Only federated addresses lowercase: `addAddressForFederation` does, while
 * `_addAddressForEmail` merely sanitizes (`_validateEMail` has no `strtolower`) and
 * `_addAddressForCatalina` stores verbatim.
 */
export function normalizeExternalAddress(address: string): string {
  const trimmed = address.trim();
  return isFederatedAddress(trimmed) ? trimmed.toLowerCase() : trimmed;
}
